import { useState } from "react";
import { format } from "date-fns";
import {
  CreditCard,
  Copy,
  Check,
  ChevronDown,
  ChevronRight,
  Ticket,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  RegistrationGroup,
  InvoiceLineItem,
} from "../../api/getEventTransactionDetails/types";
import { RefundPanel } from "./RefundPanel";
import { VoucherReturnPanel } from "./VoucherReturnPanel";

interface RegistrationDrawerProps {
  group: RegistrationGroup | null;
  open: boolean;
  onClose: () => void;
  onRefundSuccess: () => void;
}

function fmt(amount: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}

function fmtDate(dateString: string | null) {
  if (!dateString) return "—";
  try {
    return format(new Date(dateString), "MMM d, yyyy 'at' h:mm a");
  } catch {
    return dateString;
  }
}

function fmtDateShort(dateString: string | null) {
  if (!dateString) return "—";
  try {
    return format(new Date(dateString), "MMM d, yyyy");
  } catch {
    return dateString;
  }
}

function CopyableText({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <button
      className="inline-flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors font-mono"
      onClick={handleCopy}
      title="Copy to clipboard"
      type="button"
    >
      {text}
      {copied ? (
        <Check className="h-3 w-3 text-green-500" />
      ) : (
        <Copy className="h-3 w-3" />
      )}
    </button>
  );
}

function CollapsibleSection({
  title,
  defaultOpen = false,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div>
      <button
        className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors w-full text-left py-1"
        onClick={() => setOpen(!open)}
        type="button"
      >
        {open ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        {title}
      </button>
      {open && <div className="mt-2">{children}</div>}
    </div>
  );
}

export function RegistrationDrawer({
  group,
  open,
  onClose,
  onRefundSuccess,
}: RegistrationDrawerProps) {
  const [showRefundPanel, setShowRefundPanel] = useState(false);
  const [showVoucherReturnPanel, setShowVoucherReturnPanel] = useState(false);

  function handleRefundSuccess() {
    setShowRefundPanel(false);
    onRefundSuccess();
  }

  function handleVoucherReturnSuccess() {
    setShowVoucherReturnPanel(false);
    onRefundSuccess();
  }

  function handleOpenChange(isOpen: boolean) {
    if (!isOpen) {
      setShowRefundPanel(false);
      setShowVoucherReturnPanel(false);
      onClose();
    }
  }

  if (!group) return null;

  const hasRefundableLineItems = group.invoices.some((invoice) =>
    invoice.lineItems.some((item) => item.maxRefundableQuantity > 0),
  );

  const availableVouchersToReturn = group.voucherUsageHistory.filter(
    (v) => !v.isReturned,
  ).length;

  const statusInfo = getStatusInfo(group);

  return (
    <Sheet onOpenChange={handleOpenChange} open={open}>
      <SheetContent
        className="w-full sm:max-w-lg overflow-y-auto"
        side="right"
      >
        <SheetHeader className="pb-4">
          <div className="flex items-start justify-between gap-2 pr-6">
            <div>
              <SheetTitle className="text-base font-normal text-gray-900 dark:text-white">
                {group.companyName}
              </SheetTitle>
              <SheetDescription className="text-xs mt-1">
                {fmtDateShort(group.createdAt)}
                {group.confirmationNumber &&
                  ` · #${group.confirmationNumber}`}
              </SheetDescription>
            </div>
            <span
              className={`text-[10px] font-medium px-1.5 py-0.5 rounded flex-shrink-0 mt-1 ${statusInfo.className}`}
            >
              {statusInfo.label}
            </span>
          </div>
        </SheetHeader>

        <div className="space-y-5">
          {/* Financial Summary */}
          <div className="rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50 p-3 space-y-1.5">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500 dark:text-gray-400">
                Total Paid
              </span>
              <span className="font-medium text-gray-900 dark:text-white">
                {fmt(group.totalPaid)}
              </span>
            </div>
            {group.refundedAmount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">
                  Refunded
                </span>
                <span className="font-medium text-red-600 dark:text-red-400">
                  -{fmt(group.refundedAmount)}
                </span>
              </div>
            )}
            {group.pendingRefundAmount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">
                  Pending Refund
                </span>
                <span className="font-medium text-amber-600 dark:text-amber-400">
                  -{fmt(group.pendingRefundAmount)}
                </span>
              </div>
            )}
            <Separator className="!my-2" />
            <div className="flex justify-between text-sm">
              <span className="font-medium text-gray-900 dark:text-white">
                Refundable
              </span>
              <span className="font-medium text-gray-900 dark:text-white">
                {fmt(group.remainingRefundableAmount)}
              </span>
            </div>
          </div>

          {/* Payment Method */}
          {group.payments.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Payment
              </h4>
              {group.payments.map((payment) => (
                <div
                  className="flex items-start justify-between text-sm"
                  key={payment.id}
                >
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                    <span className="text-gray-900 dark:text-white">
                      {payment.cardType || "Card"} ····{" "}
                      {payment.cardLast4 || "****"}
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="text-gray-900 dark:text-white">
                      {fmt(payment.amount)}
                    </div>
                    <div className="text-xs text-gray-400 dark:text-gray-500">
                      <CopyableText text={payment.transactionId} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <Separator />

          {/* Line Items */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Line Items
              </h4>
              {hasRefundableLineItems && !showRefundPanel && (
                <Button
                  className="h-7 text-xs"
                  onClick={() => setShowRefundPanel(true)}
                  size="sm"
                  variant="outline"
                >
                  Refund
                </Button>
              )}
            </div>
            {group.invoices.map((invoice) => (
              <div key={invoice.id}>
                {group.invoices.length > 1 && (
                  <div className="text-[10px] text-gray-400 dark:text-gray-500 mb-1">
                    Invoice {invoice.invoiceNumber}
                  </div>
                )}
                <div className="rounded-lg border border-gray-200 dark:border-slate-700 divide-y divide-gray-200 dark:divide-slate-700 overflow-hidden">
                  {invoice.lineItems.map((lineItem) => (
                    <LineItemRow key={lineItem.id} lineItem={lineItem} />
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Refund Panel */}
          {showRefundPanel && (
            <RefundPanel
              group={group}
              onCancel={() => setShowRefundPanel(false)}
              onSuccess={handleRefundSuccess}
            />
          )}

          {/* Vouchers */}
          {group.voucherCount > 0 && (
            <>
              <Separator />
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    Vouchers
                  </h4>
                  {availableVouchersToReturn > 0 &&
                    !showVoucherReturnPanel && (
                      <Button
                        className="h-7 text-xs"
                        onClick={() => setShowVoucherReturnPanel(true)}
                        size="sm"
                        variant="outline"
                      >
                        Return Vouchers
                      </Button>
                    )}
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <div className="flex items-center gap-1.5">
                    <Ticket className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                    <span className="text-gray-900 dark:text-white">
                      {group.voucherCount} used
                    </span>
                  </div>
                  {availableVouchersToReturn < group.voucherCount && (
                    <span className="text-xs text-amber-600 dark:text-amber-400">
                      {group.voucherCount - availableVouchersToReturn} returned
                    </span>
                  )}
                </div>

                {showVoucherReturnPanel && (
                  <VoucherReturnPanel
                    group={group}
                    onCancel={() => setShowVoucherReturnPanel(false)}
                    onSuccess={handleVoucherReturnSuccess}
                  />
                )}
              </div>
            </>
          )}

          {/* Refund History */}
          {group.refundHistory.length > 0 && (
            <>
              <Separator />
              <CollapsibleSection
                defaultOpen={group.hasPendingRefunds}
                title={`Refund History (${group.refundHistory.length})`}
              >
                <div className="space-y-2">
                  {group.refundHistory.map((refund) => (
                    <div
                      className="rounded-lg border border-gray-200 dark:border-slate-700 p-3 text-sm"
                      key={refund.id}
                    >
                      <div className="flex items-start justify-between mb-1">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {fmt(refund.requestedAmount)}
                        </span>
                        <RefundStatusBadge status={refund.status} />
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {refund.reason}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        {refund.requestedBy} · {fmtDateShort(refund.createdAt)}
                      </p>
                      {refund.transactionId && (
                        <div className="mt-1">
                          <CopyableText text={refund.transactionId} />
                        </div>
                      )}
                      {refund.status === "failed" && refund.errorMessage && (
                        <p className="text-xs text-red-500 dark:text-red-400 mt-1">
                          {refund.errorMessage}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </CollapsibleSection>
            </>
          )}

          {/* Voucher History */}
          {group.voucherUsageHistory.length > 0 && (
            <CollapsibleSection
              title={`Voucher History (${group.voucherUsageHistory.length})`}
            >
              <div className="space-y-1.5">
                {group.voucherUsageHistory.map((v) => (
                  <div
                    className="flex items-start justify-between text-xs py-1.5"
                    key={v.id}
                  >
                    <div>
                      <span className="text-gray-900 dark:text-white">
                        {v.voucherName}
                      </span>
                      {v.attendeeName && (
                        <span className="text-gray-400 dark:text-gray-500 ml-1.5">
                          {v.attendeeName}
                        </span>
                      )}
                    </div>
                    {v.isReturned ? (
                      <span className="text-amber-600 dark:text-amber-400">
                        Returned
                      </span>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">
                        Active
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </CollapsibleSection>
          )}

          {/* Contact & Notes */}
          {(group.contactName ||
            group.contactEmail ||
            group.groupNotes) && (
            <>
              <Separator />
              <div>
                <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                  Contact & Notes
                </h4>
                <div className="space-y-2 text-sm">
                  {group.contactName && (
                    <div className="text-gray-900 dark:text-white">
                      {group.contactName}
                    </div>
                  )}
                  {group.contactEmail && (
                    <div className="text-gray-500 dark:text-gray-400">
                      {group.contactEmail}
                    </div>
                  )}
                  {group.contactPhone && (
                    <div className="text-gray-500 dark:text-gray-400">
                      {group.contactPhone}
                    </div>
                  )}
                  {group.groupNotes && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 italic whitespace-pre-wrap mt-2">
                      {group.groupNotes}
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function LineItemRow({ lineItem }: { lineItem: InvoiceLineItem }) {
  const isNegative = lineItem.unitPrice < 0;
  const isFullyRefunded = lineItem.isFullyRefunded && !isNegative;
  const hasPartialRefund =
    lineItem.totalRefundedQuantity > 0 && !isFullyRefunded && !isNegative;

  return (
    <div
      className={`px-3 py-2 ${isFullyRefunded ? "bg-gray-50 dark:bg-slate-800/50" : "bg-white dark:bg-slate-800"}`}
    >
      <div className="flex justify-between items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span
              className={`text-sm truncate ${isFullyRefunded ? "text-gray-400 dark:text-gray-500 line-through" : "text-gray-900 dark:text-white"}`}
            >
              {lineItem.description}
            </span>
            {isFullyRefunded && (
              <Badge
                className="text-[10px] px-1 py-0"
                variant="secondary"
              >
                Refunded
              </Badge>
            )}
            {hasPartialRefund && (
              <Badge className="text-[10px] px-1 py-0" variant="outline">
                Partial
              </Badge>
            )}
          </div>
          <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            {lineItem.quantity} × {fmt(lineItem.unitPrice)}
          </div>
        </div>
        <span
          className={`text-sm font-medium flex-shrink-0 ${isNegative ? "text-green-600 dark:text-green-400" : "text-gray-900 dark:text-white"}`}
        >
          {fmt(lineItem.lineTotal)}
        </span>
      </div>
    </div>
  );
}

function RefundStatusBadge({
  status,
}: {
  status: "pending" | "processing" | "completed" | "failed";
}) {
  const styles = {
    completed:
      "bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-300",
    pending:
      "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    processing:
      "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    failed:
      "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  };

  return (
    <span
      className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${styles[status]}`}
    >
      {status}
    </span>
  );
}

function getStatusInfo(group: RegistrationGroup) {
  if (group.hasPendingRefunds) {
    return {
      label: "Pending Refund",
      className:
        "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    };
  }
  if (group.refundedAmount > 0 && group.remainingRefundableAmount <= 0) {
    return {
      label: "Fully Refunded",
      className:
        "bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-300",
    };
  }
  if (group.refundedAmount > 0) {
    return {
      label: "Partially Refunded",
      className:
        "bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-300",
    };
  }
  return {
    label: "Paid",
    className:
      "bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-300",
  };
}
